import { useQuery } from '@apollo/client';

import { gengql } from '@/__generated__';
import type { ActionSearchQueryVariables } from '@/__generated__/graphql';
import { Query } from '@/search';
import { buildSearchVariables } from '@/search/store';

// Identical signature/args to actionSearch (src/search/store/index.ts) with a
// count-only selection, and variables built by the same buildSearchVariables —
// so this total is computed by exactly the query "Browse your full network"
// runs and can never disagree with it. ENG-5249 hid the previous headline
// count for that exact disagreement (DB row count vs search index).
const NETWORK_EXPERT_COUNT = gengql(/* GraphQL */ `
  query dashboardNetworkExpertCount(
    $cursor: String
    $text: String
    $sort: String
    $ascending: Boolean
    $highlight: Boolean
    $countries: [String!]
    $languages: [String!]
    $industries: [String!]
    $mustHaveEmail: Boolean
    $profileTypes: [ProfileType]
    $expertStates: [ExpertState]
    $expertHistory: [ExpertHistory]
    $agentIds: [String!]
    $networkIds: [String!]
    $sharingGroupIds: [ID!]
    $experienceOrganizations: ExperienceFieldInput
    $experienceLocations: ExperienceFieldInput
    $experienceJobTitles: ExperienceFieldInput
    $consultationsWith: ConsultationsWithInput
    $educationDegrees: [String!]
    $educationFos: [String!]
    $groupKeywordIds: [ID!]
    $keywords: [String!]
    $marketplacePreference: MarketplacePreference
  ) {
    search(
      after: $cursor
      query: $text
      sort: $sort
      ascending: $ascending
      highlight: $highlight
      countries: $countries
      languages: $languages
      industries: $industries
      must_have_email: $mustHaveEmail
      profile_types: $profileTypes
      expert_states: $expertStates
      expert_history: $expertHistory
      agent_ids: $agentIds
      network_ids: $networkIds
      sharing_group_ids: $sharingGroupIds
      experience_organizations: $experienceOrganizations
      experience_locations: $experienceLocations
      experience_job_titles: $experienceJobTitles
      consultations_with: $consultationsWith
      education_degrees: $educationDegrees
      education_fos: $educationFos
      group_keyword_ids: $groupKeywordIds
      keywords: $keywords
      marketplace_preference: $marketplacePreference
    ) {
      pageInfo {
        total
      }
    }
  }
`);

/**
 * The variables a bare `/search?networks[]=<id>` landing produces. The Query
 * constructor defaults `mustHaveEmail` to `true`, but SearchPage parses the
 * missing `email` URL param as null (no filter) — the API only applies the
 * email filter on an explicit `true`. Browse transmits `mustHaveEmail: null`;
 * we omit the key (undefined). Not byte-identical, but equivalent: the API
 * resolver type-asserts both absent and null to false (search_gql.go,
 * `p.Args["must_have_email"].(bool)`), so neither applies the email filter.
 * Without this override the count filters to experts with an email and
 * undercounts vs the Browse results.
 */
export function browseLandingVariables(internalNetworkId: string): ActionSearchQueryVariables {
  const query = new Query({ networkIds: [internalNetworkId] });
  query.mustHaveEmail = undefined;
  return buildSearchVariables(query);
}

interface NetworkExpertCount {
  count: number;
  loading: boolean;
  error: boolean;
  /**
   * Underlying ApolloError, for DashboardCard's Sentry reporting. Captured
   * only when the error branch renders (no cached count to show) — background
   * refetch failures with stale data visible are deliberately not reported;
   * cold-load failures still are, which is where infra degradation surfaces.
   */
  errorDetail?: unknown;
  refetch: () => void;
}

export function useNetworkExpertCount(internalNetworkId: string): NetworkExpertCount {
  const { data, loading, error, refetch } = useQuery(NETWORK_EXPERT_COUNT, {
    variables: browseLandingVariables(internalNetworkId),
    fetchPolicy: 'cache-and-network',
  });

  return {
    count: data?.search?.pageInfo?.total ?? 0,
    loading,
    // A failed background refetch shouldn't replace an already-shown count
    // with the error state — only error when there's nothing to show.
    error: !!error && !data,
    errorDetail: error,
    refetch: () => {
      // Apollo surfaces refetch failures via the hook's error state; the
      // catch only prevents an unhandled promise rejection on retry.
      void refetch().catch(() => undefined);
    },
  };
}
